import React, { useState, useEffect, useRef } from "react";
import { MapContainer, TileLayer, Polygon, FeatureGroup, Marker, Popup, Tooltip, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import "./Styles.css";
import useGeoLocation from "../utils/useGeoLocation";
import L from 'leaflet';
import { ScaleControl } from 'react-leaflet';
// import Legend from "./Legend";
import { Row } from "react-bootstrap";
import Legend from "../components/Legend";
import defaultLeafletMarkerIcon from "../utils/leafletConfig";
import FullscreenRoundedIcon from "@mui/icons-material/FullscreenRounded";
import FullscreenExitRoundedIcon from "@mui/icons-material/FullscreenExitRounded";

const hoverMarkerIcon = L.divIcon({
  className: "satellite-hover-marker",
  iconSize: [1, 1],
});

const CursorValueListener = ({ onMove }) => {
  useMapEvents({ mousemove: (event) => onMove(event.latlng) });
  return null;
};

const isPointInsidePolygon = (latitude, longitude, coordinates) => {
  let inside = false;
  for (let i = 0, j = coordinates.length - 1; i < coordinates.length; j = i++) {
    const [lat1, lon1] = coordinates[i];
    const [lat2, lon2] = coordinates[j];
    const intersects =
      lon1 > longitude !== lon2 > longitude &&
      latitude < ((lat2 - lat1) * (longitude - lon1)) / (lon2 - lon1) + lat1;
    if (intersects) inside = !inside;
  }
  return inside;
};

const SatteliteMap = ({
  data,
  selectedImageUrl,
  renderImageGallery,
  renderDateNavigator,
  renderMapLayerControls,
  selectedImageType,
  onIndicatorHover,
  pixelValue,
}) => {
  const location = useGeoLocation();
  const [polygonCoords, setPolygonCoords] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [mapCenter, setMapCenter] = useState([36.806389, 10.181667]);
  const [zoomLevel, setZoomLevel] = useState(10);
  const mapRef = useRef(null);
  const imageOverlayRef = useRef(null);
  const hoverTimerRef = useRef(null);
  const [bounds, setBounds] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Initialize map with field data
  useEffect(() => {
    if (data && data.length > 0 && data[0]) {
      const field = data[0];
      const latitude = Number(field.Latitude);
      const longitude = Number(field.Longitude);

      // Set marker position
      setMarkers([latitude, longitude]);
      setMapCenter([latitude, longitude]);

      // Parse and set polygon coordinates
      try {
        if (field.coordinates) {
          const parsedCoords = JSON.parse(field.coordinates);

          // Ensure that parsedCoords is an array of objects like [{Lat: xx, Long: xx}, ...]
          if (Array.isArray(parsedCoords)) {
            const formattedCoords = parsedCoords.map(coord => {
              return [coord.Lat, coord.Long]; // Ensure [Lat, Long] format for Leaflet
            });

            setPolygonCoords(formattedCoords); // Set the coordinates state
            calculateImageBounds(formattedCoords); // Calculate the bounds based on the coordinates
          }
        }
      } catch (e) {
        console.error("Error parsing coordinates:", e);
      }
    }
  }, [data]);


  // Handle selected image URL changes
  useEffect(() => {
    if (selectedImageUrl && bounds && mapRef.current) {
      // Remove previous overlay if exists
      if (imageOverlayRef.current) {
        imageOverlayRef.current.remove();
      }

      // Create new image overlay
      imageOverlayRef.current = L.imageOverlay(selectedImageUrl, bounds, {
        opacity: 1,
        interactive: true,
      }).addTo(mapRef.current);

      // Fit bounds to show the entire image
      mapRef.current.fitBounds(bounds);
    }

    return () => {
      if (imageOverlayRef.current) {
        imageOverlayRef.current.remove();
      }
    };
  }, [selectedImageUrl, bounds]);

  // Calculate bounds from polygon coordinates
  // Calculate bounds from polygon coordinates
  const calculateImageBounds = (coordinates) => {
    if (!coordinates || coordinates.length === 0) return;

    const lats = coordinates.map(coord => coord[0]);
    const lngs = coordinates.map(coord => coord[1]);

    const newBounds = [
      [Math.min(...lats), Math.min(...lngs)], // South West corner
      [Math.max(...lats), Math.max(...lngs)]  // North East corner
    ];

    setBounds(newBounds); // Update the bounds state
  };

  const getCenterFromField = () => {
    if (data && data.length > 0) {
      const firstField = data[0];
      return [Number(firstField.Latitude), Number(firstField.Longitude)];
    }
    return null;
  };

  const handleMapMouseMove = ({ lat, lng }) => {
    if (
      !selectedImageType ||
      !selectedImageUrl ||
      !isPointInsidePolygon(lat, lng, polygonCoords)
    ) {
      clearTimeout(hoverTimerRef.current);
      onIndicatorHover?.(null);
      return;
    }

    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      onIndicatorHover?.({ latitude: lat, longitude: lng });
    }, 350);
  };

  useEffect(() => () => clearTimeout(hoverTimerRef.current), []);
  useEffect(() => {
    const resizeTimer = setTimeout(() => mapRef.current?.invalidateSize(), 0);
    if (!isFullscreen) return () => clearTimeout(resizeTimer);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key === "Escape") setIsFullscreen(false);
    };
    document.addEventListener("keydown", closeOnEscape);

    return () => {
      clearTimeout(resizeTimer);
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", closeOnEscape);
      setTimeout(() => mapRef.current?.invalidateSize(), 0);
    };
  }, [isFullscreen]);

  useEffect(() => {
    const updateMapCenter = async () => {
      const centerFromFields = getCenterFromField();
      if (centerFromFields) {
        setMapCenter(centerFromFields);
        setZoomLevel(17)
        mapRef.current?.setView(centerFromFields, zoomLevel);
      }
    };

    updateMapCenter();
  }, [data, mapRef, zoomLevel]);


  return (
    <div className={`satellite-map-component ${isFullscreen ? "is-fullscreen" : ""}`}>
      <div className="satellite-map-frame">
        <MapContainer
          ref={mapRef}
          className="markercluster-map"
          zoom={zoomLevel}
          center={mapCenter}
          whenCreated={(map) => {
            mapRef.current = map;
          }}
        >
        <CursorValueListener onMove={handleMapMouseMove} />
        <FeatureGroup>
          <ScaleControl position="bottomleft" />
        </FeatureGroup>

        <TileLayer
          maxNativeZoom={18}
          maxZoom={20}
          url="https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}"
          subdomains={['mt0', 'mt1', 'mt2', 'mt3']}
        />

        {/* <Legend map={mapRef.current} /> */}
        {/* <LeafletGeoCoder /> */}

        {location.loaded && !location.error && (
          <Marker
            position={[location.coordinates.lat, location.coordinates.lng]}
            icon={defaultLeafletMarkerIcon}
          >
            <Popup>My position</Popup>
          </Marker>
        )}

        {markers.length > 0 && data && data[0] && (
          <Marker
            position={[markers[0], markers[1]]}
            icon={defaultLeafletMarkerIcon}
          >
            <Popup>{data[0].title}</Popup>
          </Marker>
        )}

        {polygonCoords.length > 0 && (
          <Polygon
            pathOptions={{ color: '#28A6B7', weight: 2, fillOpacity: 0.12 }}

            positions={polygonCoords}
            fillColor="none"
          />
        )}
        {pixelValue &&
          Number.isFinite(Number(pixelValue.latitude)) &&
          Number.isFinite(Number(pixelValue.longitude)) && (
          <Marker
            position={[pixelValue.latitude, pixelValue.longitude]}
            icon={hoverMarkerIcon}
            interactive={false}
          >
            <Tooltip permanent direction="top" offset={[0, -8]} className="satellite-pixel-tooltip">
              {pixelValue.status === "loading" && "Chargement…"}
              {pixelValue.status === "error" && pixelValue.message}
              {pixelValue.status !== "loading" &&
                pixelValue.status !== "error" &&
                pixelValue.value !== undefined && (
                  <>
                    <strong>{pixelValue.type.toUpperCase()}</strong>: {Number(pixelValue.value).toFixed(4)} {pixelValue.unit}
                  </>
                )}
            </Tooltip>
          </Marker>
        )}
          <Legend map={mapRef.current} selectedType={selectedImageType} />
        </MapContainer>
        <button
          type="button"
          className="satellite-map-fullscreen-button"
          onClick={() => setIsFullscreen((fullscreen) => !fullscreen)}
          aria-label={isFullscreen ? "Réduire la carte" : "Agrandir la carte"}
          title={isFullscreen ? "Réduire la carte" : "Agrandir la carte"}
        >
          {isFullscreen ? <FullscreenExitRoundedIcon /> : <FullscreenRoundedIcon />}
        </button>
        {isFullscreen && renderMapLayerControls?.()}
      </div>
      {!isFullscreen && (
        <>
          {renderDateNavigator?.()}
          <Row className="satellite-gallery-row">
            {renderImageGallery()}
          </Row>
        </>
      )}

    </div>
  );
};

export default SatteliteMap;
